import axios from 'axios';
import { merge } from 'lodash';
import WildEmitter from 'wildemitter';
import socketio from 'socket.io-client';
import Stash from 'src/stash';
import moment from 'moment';
import bugsnag from './bugsnag';

class SocketManager {
  constructor(host) {
    this.theSocket = socketio(host, {
      reconnect: true,
      'reconnection delay': 5,
      'max reconnection attempts': 1000,
    });
  }

  get socket() {
    return this.theSocket;
  }

  on(messages, callback) {
    console.log('IO.ON', messages);

    return [messages]
      .reduce((a, b) => a.concat(b), [])
      .map(message => this.socket.on(message, callback));
  }

  emit(room, event, data = {}) {
    console.log('IO.EMIT', room, event, data);

    return this.socket.emit(event, { room, data });
  }
}

export const IO = new SocketManager(process.env.BROADCAST_URL);

export const Emitter = new WildEmitter();

export const Api = {
  get(path, options = {}) {
    return this.request('get', path, options);
  },

  post(path, options = {}) {
    return this.request('post', path, options);
  },

  put(path, options = {}) {
    return this.request('put', path, options);
  },

  delete(path, options = {}) {
    return this.request('delete', path, options);
  },

  request(method, path, overrides) {
    const defaults = {
      method,
      url: this.endpoint(path),
      headers: { Accept: 'application/json' },
      responseType: 'json',
      withCredentials: true,
      channel: 'background',
    };

    const options = merge(defaults, overrides);

    Emitter.emit('request:start', { channel: options.channel });

    return axios(options)
      .then(response => {
        Emitter.emit('request:end', { channel: options.channel });

        return response;
      })
      .catch(error => {
        Emitter.emit('request:end', { channel: options.channel });

        throw error;
      });
  },

  endpoint(path) {
    return [process.env.API_URL, path.replace(/^\//, '')].join('/');
  },

  // Depending on the SFU's order visibility settings, socket.io events
  // may be received for orders and items the SFU is not configured to show.
  //
  // When the SFU attempts to fetch order or item data in these situations,
  // the response will be a 403 Forbidden error.
  //
  // @link https://trello.com/c/jZmh0ZS0
  handleVisibilityError(error) {
    if (error.response && Number(error.response.status) === 403) {
      return;
    }

    throw error;
  },
};

//
// Calculate the best rational approximation of a decimal.
// @link http://www.johndcook.com/blog/2010/10/20/best-rational-approximation/
//
export const farey = (x, n) => {
  let [a, b] = [0, 1];
  let [c, d] = [1, 1];
  let mediant;

  while (b <= n && d <= n) {
    mediant = (a + c) / (b + d);

    if (x === mediant) {
      if (b + d <= n) {
        return [a + c, b + d];
      }

      if (d > b) {
        return [c, d];
      }

      return [a, b];
    }

    if (x > mediant) {
      [a, b] = [a + c, b + d];

      continue;
    }

    [c, d] = [a + c, b + d];
  }

  if (b > n) {
    return [c, d];
  }

  return [a, b];
};

export const isNonZero = value => {
  if (isNaN(value)) {
    return false;
  }

  return Number(value) !== 0;
};

/**
 * Convert a value that may be a float like 3.23 or cents like 323 into
 */
export const asCents = (input, options = {}) => {
  const opts = { allowNegative: false, ...options };
  const value = Number(String(input).trim() || '');

  if (Number(value) === NaN) {
    return 0;
  }

  if (value.length === 0) {
    return 0;
  }

  if (!opts.allowNegative && Number(value) <= 0) {
    return 0;
  }

  return Math.round(Number(value).toFixed(2) * 100);
};

export const asCurrency = value => {
  if (Number(value) === NaN) {
    return 0;
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(/\./.test(value) ? value : value / 100);
};

export const printLabel = (id, opts = {}) => {
  const options = { auto: false, ...opts };

  return Api.get(`orders/${id}/label`, {
    params: { include: 'store' },
  }).then(response => {
    if (options.auto && response.status !== 201) {
      // if the SFU is trying to auto-print a label for an order and a 200
      // status comes back with the response, we do NOT send the print request
      // to the printer.
      //
      // A 201 means the order has not had a label rinted yet. A 200 means it has,
      // but is OK to do so again.
      return;
    }

    const attrs = {
      label_printed_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
    };

    Stash.updateOrder(id, attrs);

    return axios.post(process.env.LABEL_PRINTER_URL, response.data, {
      headers: {
        Authentication: process.env.LABEL_PRINTER_API_KEY,
        'Content-Type': 'application/json',
      },
    });
  });
};

// Chain a series of promises to sequentially process order printing for each
// order in the provided collection that have not already had a label printed.
//
// Whether labels print or not, we mark them in the stash as having had the auto
// -print attempted. This avoids attempts on the same orders that fail for
// an unknown reason from being attempted every minute. For example, if the
// print call fails, this prevents repeated attempts to pull label JSON from our
// API every minute.
export const printLabels = orders =>
  orders
    .filter(o => o.label_printed_at === null)
    .filter(o => !o.auto_print_attempted)
    .reduce(
      (acc, o) =>
        acc
          .then(() => printLabel(o.id, { auto: true }))
          .catch(() => void 0)
          .then(() => Stash.updateOrder(o.id, { auto_print_attempted: true })),
      Promise.resolve()
    );

export const reboot = () => {
  console.log('Rebooting!');
  window.location = `/?${Number(new Date())}`;
};



// WEBPACK FOOTER //
// ./src/utilities.js